/**
 * ACDBASSOCVARIABLE reader — DYN-4 Phase 2a (Fix 3).
 *
 * AutoCAD 2018+ parametric dynamic blocks (e.g. the "Двутавр ГОСТ 8239-89"
 * ibeam block) expose their driving scalars as `ACDBASSOCVARIABLE` records
 * in the OBJECTS section. Each variable carries a human-readable name
 * (group 1[0]), a serialised current-value string (group 1[1]), an opaque
 * `AcDbCalc:1.0` expression payload (group 1[2] + 1[3]), and a numeric
 * snapshot of the value (group 40). A `BLOCKUSERPARAMETER` inside the
 * block's authoring graph references the variable by handle (group 330
 * after the `AcDbBlockUserParameter` subclass marker) so the evaluator
 * can resolve the user-visible parameter name + default value.
 *
 * This module pre-scans the OBJECTS section groups and returns a
 * `Map<handle, AssocVariable>` so the downstream BLOCKUSERPARAMETER
 * parser (see `packages/drawing-model/src/dxfBridge.dynamic.parser.userParam.ts`)
 * can look up each variable by its 330-referenced handle in O(1).
 *
 * The parser is tolerant:
 *   - Missing fields fall back to sane defaults (empty strings, 0).
 *   - Unknown `90` value-type codes are mapped to `"unknown"` so
 *     downstream consumers can decide how to display them.
 *   - The full raw group list is preserved on `AssocVariable.rawGroups`
 *     so a subsequent writer pass can re-emit the record verbatim.
 *
 * Scope boundary: this module only decodes the shape needed to wire
 * BLOCKUSERPARAMETER → user-visible name / default. Evaluation of the
 * `AcDbCalc:1.0` expression payload is out of scope (a later DYN-4 phase
 * reintroduces it via `ParamDef.expression`).
 */
import type { DxfGroup } from "./types";
import { decodeDxfUnicode } from "./dxfUnicodeEscape";

/**
 * Decoded shape of an ACDBASSOCVARIABLE record. `valueType` follows the
 * AutoCAD internal mapping documented in `AcDbAssocVariable.h`:
 *   0 → real, 1 → distance, 2 → area, 3 → angle, 5 → string.
 * The `"unknown"` bucket catches everything else (reserved / vendor
 * extensions) so downstream consumers can decide whether to expose the
 * parameter or hide it.
 */
export interface AssocVariable {
  /** Self handle (group 5). Always non-empty for well-formed DXF. */
  handle: string;
  /**
   * Owner handle (first group 330 after the entity header). For variables
   * that belong to a block's associative network this is the
   * `ACDBASSOCNETWORK` handle; leaf variables may point directly at the
   * block record's extension dictionary.
   */
  ownerHandle: string;
  /** Variable name — the key column value shown in AutoCAD's Block Editor. */
  name: string;
  /** Decoded value type (see class-level doc). */
  valueType: "real" | "distance" | "area" | "angle" | "string" | "unknown";
  /**
   * Best-effort decode of the current value. Numeric for `real` / `distance`
   * / `area` / `angle`; string for `string`; defaults to `0` when no numeric
   * snapshot is present.
   */
  currentValue: number | string;
  /**
   * Opaque `AcDbCalc:1.0` payload (group 1[2]) — preserved so a later
   * expression evaluator can run against it. Omitted when the variable
   * has no expression (pure literal).
   */
  expression?: string;
  /** Full raw group list for verbatim re-emission via the writer. */
  rawGroups: DxfGroup[];
}

/**
 * Pre-scan the OBJECTS section groups and return a `Map<handle, AssocVariable>`
 * for every `ACDBASSOCVARIABLE` entity found inside it.
 *
 * The input is the raw DxfGroup[] of the OBJECTS section (typically
 * `DxfObjectsSection.rawObjectGroups`, or any other flat group list that
 * spans `code: 0` entity headers). The scanner locates each
 * `ACDBASSOCVARIABLE` header, collects every group until the next
 * `code: 0`, and hands the slice to `decodeAssocVariable`.
 *
 * Returns an empty map when no ACDBASSOCVARIABLE records are present —
 * this is the common case for static blocks.
 */
export function collectAssocVariables(
  groups: readonly DxfGroup[],
): Map<string, AssocVariable> {
  const out = new Map<string, AssocVariable>();

  let current: DxfGroup[] | null = null;
  let currentIsAssoc = false;

  const flush = (): void => {
    if (!currentIsAssoc || current === null) return;
    const decoded = decodeAssocVariable(current);
    if (decoded.handle) out.set(decoded.handle, decoded);
  };

  for (const g of groups) {
    if (g.code === 0) {
      // Finish the previous record before starting a new one.
      flush();
      const type = g.value.trim();
      currentIsAssoc = type === "ACDBASSOCVARIABLE";
      current = [g];
      continue;
    }
    if (current) current.push(g);
  }
  // Flush trailing record (no terminating `code: 0` in malformed sources).
  flush();

  return out;
}

/**
 * Decode a single ACDBASSOCVARIABLE body (the full group list, including
 * the `code: 0` header). Exported for unit-test convenience — production
 * code should use `collectAssocVariables`.
 */
export function decodeAssocVariable(groups: DxfGroup[]): AssocVariable {
  let handle = "";
  let ownerHandle = "";
  let name = "";
  let valueTypeRaw: number | undefined;
  let currentValueNumeric: number | undefined;
  let currentValueString: string | undefined;
  let expression: string | undefined;

  // Track position relative to the `AcDbAssocVariable` subclass marker so
  // we pick the right group 330 (first 330 after the marker = owner of the
  // variable-specific block, NOT the parent AcDbAssocAction).
  let pastVariableMarker = false;
  // AutoCAD emits up to four group-1 strings per ACDBASSOCVARIABLE:
  //   1[0] = name, 1[1] = value string, 1[2] = AcDbCalc payload, 1[3] = extra.
  const group1Values: string[] = [];

  for (const g of groups) {
    switch (g.code) {
      case 5:
        if (handle === "") handle = g.value.trim();
        break;
      case 100:
        if (g.value === "AcDbAssocVariable") pastVariableMarker = true;
        break;
      case 330:
        // First 330 before the AcDbAssocVariable marker is the owner.
        if (ownerHandle === "" && !pastVariableMarker) {
          ownerHandle = g.value.trim();
        }
        break;
      case 1:
        group1Values.push(g.value);
        break;
      case 90:
        // Inside AcDbAssocVariable block, code 90 is the value-type flag.
        if (pastVariableMarker && valueTypeRaw === undefined) {
          const n = Number.parseInt(g.value.trim(), 10);
          if (Number.isFinite(n)) valueTypeRaw = n;
        }
        break;
      case 40:
        if (currentValueNumeric === undefined) {
          const n = Number.parseFloat(g.value);
          if (Number.isFinite(n)) currentValueNumeric = n;
        }
        break;
      default:
        break;
    }
  }

  // Group-1 slot assignment:
  //   [0] name, [1] current-value-as-string, [2] expression (AcDbCalc:…),
  //   [3] trailing / optional suffix.
  // T9 PR-3 — code-1 content is `\U+`-escaped on the synthetic write path
  // (rawGroups passthrough stays verbatim, so byte-identity is unaffected);
  // decode the typed CONSUMER-facing fields so they read back as real chars.
  if (group1Values[0] !== undefined) name = decodeDxfUnicode(group1Values[0].trim());
  if (group1Values[1] !== undefined) currentValueString = decodeDxfUnicode(group1Values[1]);
  if (group1Values[2] !== undefined && group1Values[2].startsWith("AcDbCalc:")) {
    expression = decodeDxfUnicode(group1Values[2]);
  }

  const valueType = mapValueType(valueTypeRaw);

  // Numeric snapshot (40) wins over the stringified value when the type
  // is numeric; otherwise fall back to the string form.
  const currentValue: number | string =
    valueType === "string"
      ? (currentValueString ?? "")
      : (currentValueNumeric ?? parseNumericFallback(currentValueString) ?? 0);

  const result: AssocVariable = {
    handle,
    ownerHandle,
    name,
    valueType,
    currentValue,
    rawGroups: groups,
  };
  if (expression !== undefined) result.expression = expression;
  return result;
}

function mapValueType(raw: number | undefined): AssocVariable["valueType"] {
  switch (raw) {
    case 0:
      return "real";
    case 1:
      return "distance";
    case 2:
      return "area";
    case 3:
      return "angle";
    case 5:
      return "string";
    default:
      return "unknown";
  }
}

function parseNumericFallback(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : undefined;
}
